import moment from 'moment';
import ContractService from '@/services-http/contract/ContractService';

export default {
  name: 'MovementDeadlineMixin',

  data: () => ({
    movementDeadlineContractService: null,
    movementDeadlineOperationsByContractId: {},
  }),

  methods: {
    getMovementDeadlineOperations(movementDeadlineOperations) {
      return Array.isArray(movementDeadlineOperations) ? movementDeadlineOperations : [];
    },

    getMovementDeadlineContractService() {
      if (!this.movementDeadlineContractService) {
        this.movementDeadlineContractService = new ContractService();
      }

      return this.movementDeadlineContractService;
    },

    async getMovementDeadlineOperationsByContractId(contractId) {
      if (!contractId) return [];

      const cachedMovementDeadlineOperations = this.movementDeadlineOperationsByContractId[contractId];

      if (Array.isArray(cachedMovementDeadlineOperations)) {
        return cachedMovementDeadlineOperations;
      }

      const movementDeadlineOperations = await this.getMovementDeadlineContractService()
        .FindMovementDeadlineOperationsByContractId(contractId)
        .then((response) => (response && Array.isArray(response.data) ? response.data : []))
        .catch(() => []);

      this.$set(
        this.movementDeadlineOperationsByContractId,
        contractId,
        movementDeadlineOperations,
      );

      return movementDeadlineOperations;
    },

    async getMovementDeadlineConfigByContractId(contractId, movementType, cutOffDate = null) {
      const movementDeadlineOperations = await this.getMovementDeadlineOperationsByContractId(contractId);

      return this.getMovementDeadlineConfig(movementDeadlineOperations, movementType, cutOffDate);
    },

    getMovementDeadlineConfig(movementDeadlineOperations, movementType, cutOffDate = null) {
      if (!movementType) return null;

      const normalizedMovementType = movementType.toString().trim().toLowerCase();
      const movementDeadlineOperation = this.getMovementDeadlineOperations(movementDeadlineOperations).find((item) => {
        const itemMovementType = item ? item.movement_type : null;

        return itemMovementType
          && itemMovementType.toString().trim().toLowerCase() === normalizedMovementType;
      });

      if (!movementDeadlineOperation) return null;

      return {
        id: movementDeadlineOperation.id ?? null,
        movement_type: movementDeadlineOperation.movement_type || null,
        days_in_advance: movementDeadlineOperation.days_in_advance ?? null,
        contract_id: movementDeadlineOperation.contract_id ?? null,
        cut_off_date: cutOffDate ?? null,
        use_cut_off_date: Boolean(movementDeadlineOperation.use_cut_off_date),
      };
    },

    hasMovementDeadlineConfig(movementDeadlineOperations, movementType, cutOffDate = null) {
      return Boolean(this.getMovementDeadlineConfig(movementDeadlineOperations, movementType, cutOffDate));
    },

    setMovementDeadlineValue(target, key, value) {
      if (!target || typeof target !== 'object') return;

      if (this.$set) {
        this.$set(target, key, value);
        return;
      }

      target[key] = value;
    },

    setMovementDeadlineLoading(target, value) {
      this.setMovementDeadlineValue(target, 'loadingScheduledReleaseDate', Boolean(value));
    },

    async syncMovementDeadlineView() {
      if (this.$nextTick) {
        await this.$nextTick();
      }

      if (this.$forceUpdate) {
        this.$forceUpdate();

        if (this.$nextTick) {
          await this.$nextTick();
        }
      }
    },

    getMovementDeadlineFieldRenderKey(
      movementDeadlineConfig,
      scheduledReleaseDate,
      loadingScheduledReleaseDate,
      referenceKey = '',
    ) {
      const configId = movementDeadlineConfig && movementDeadlineConfig.id
        ? movementDeadlineConfig.id
        : movementDeadlineConfig && movementDeadlineConfig.contract_id
          ? movementDeadlineConfig.contract_id
          : 'no-config';
      const dateValue = scheduledReleaseDate || 'empty';
      const loadingValue = loadingScheduledReleaseDate ? 'loading' : 'idle';

      return [referenceKey, configId, dateValue, loadingValue]
        .filter((value) => value !== null && value !== undefined && value !== '')
        .join('-');
    },

    shouldRenderMovementDeadlineField(
      scheduledReleaseDate,
      showScheduledReleaseDate,
      loadingScheduledReleaseDate,
    ) {
      return Boolean(
        scheduledReleaseDate
        || showScheduledReleaseDate
        || loadingScheduledReleaseDate,
      );
    },

    isMovementDeadlineDateAfterToday(referenceDate) {
      if (!referenceDate) return false;

      const parsedReferenceDate = moment.isMoment(referenceDate)
        ? referenceDate.clone()
        : moment(referenceDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);

      if (!parsedReferenceDate.isValid()) return false;

      return parsedReferenceDate.isAfter(moment().startOf('day'), 'day');
    },

    shouldShowMovementDeadlineField(referenceDate, movementDeadlineConfig) {
      return Boolean(movementDeadlineConfig) && this.isMovementDeadlineDateAfterToday(referenceDate);
    },

    shouldShowMovementDeadlineCutOffDate(movementDeadlineConfig) {
      if (!movementDeadlineConfig) return false;

      const cutOffDate = movementDeadlineConfig.cut_off_date;

      return Boolean(
        movementDeadlineConfig.use_cut_off_date
        && cutOffDate !== null
        && cutOffDate !== undefined
        && cutOffDate.toString().trim().length > 0,
      );
    },

    getMovementDeadlineCutOffDate(movementDeadlineConfig) {
      return this.shouldShowMovementDeadlineCutOffDate(movementDeadlineConfig)
        ? movementDeadlineConfig.cut_off_date.toString().trim()
        : '';
    },

    getMovementDeadlineTooltip(referenceDate, movementDeadlineConfig) {
      if (!movementDeadlineConfig) return '';

      const cutOffDate = movementDeadlineConfig.cut_off_date ?? '-';
      const cutOffDateDescription = movementDeadlineConfig.use_cut_off_date ? `Dia de corte ${cutOffDate}` : 'Sem dia de corte';
      const dayInAdvance = movementDeadlineConfig.days_in_advance ?? '-';

      return `${cutOffDateDescription} com  ${dayInAdvance} dias de antecipação`;
    },
  },

  created() {
    this.movementDeadlineContractService = new ContractService();
  },
};
